// Raporlar ve SLA: C58–C68, B01 (SLA özet), B19, B21–B22; PDF/Excel dışa aktarma: Zod girdileri ve yanıt tipleri (API ve konsol aynı tipi kullanır).
// Tüm raporlar aynı şekli döndürür (KPI + grafik + tablo + not) — tek rapor sayfası ve tek dışa aktarma şablonu.
// Tanımlar Control ve kurum ekranlarında aynıdır: zamanında = sözleşmedeki "en fazla gecikme" içinde tamamlanan sefer.
import { z } from 'zod';
import { DIRECTIONS, type Direction } from '../enums';
import type { ServicePerformance } from './business';
import { IsoDate } from './common';

// ───────── katalog ─────────

export const REPORT_TYPES = [
  'on-time',
  'occupancy',
  'no-show',
  'eta-accuracy',
  'vehicle-utilization',
  'driver-performance',
  'complaints',
  'route-performance',
  'cost-per-passenger',
  'co2',
] as const;
export type ReportType = (typeof REPORT_TYPES)[number];

export type ReportGroup = 'QUALITY' | 'CAPACITY' | 'COST';
export const REPORT_GROUP_LABELS: Record<ReportGroup, string> = {
  QUALITY: 'Hizmet kalitesi',
  CAPACITY: 'Kapasite ve filo',
  COST: 'Maliyet ve sürdürülebilirlik',
};

export interface ReportMeta {
  type: ReportType;
  code: string;
  /** kurum ekranındaki karşılığı (B21/B22) */
  businessCode: string | null;
  title: string;
  /** raporun yanıtladığı soru (katalog kartı ve sayfa alt başlığı) */
  question: string;
  icon: 'clock' | 'yolcu' | 'x' | 'navigation' | 'arac' | 'sofor' | 'uyari' | 'rota' | 'finans' | 'spark';
  group: ReportGroup;
  /** kurum (Business/School) panelinde de görünür */
  institution: boolean;
  flag?: 'FINANCE';
}

export const REPORT_META: Record<ReportType, ReportMeta> = {
  'on-time': {
    type: 'on-time',
    code: 'C59',
    businessCode: 'B21',
    title: 'Zamanında varış',
    question: 'Seferler sözleşmedeki gecikme sınırı içinde tamamlanıyor mu?',
    icon: 'clock',
    group: 'QUALITY',
    institution: true,
  },
  occupancy: {
    type: 'occupancy',
    code: 'C60',
    businessCode: 'B21',
    title: 'Doluluk',
    question: 'Araçlar ne kadar dolu gidiyor, hangi rotada boş koltuk var?',
    icon: 'yolcu',
    group: 'CAPACITY',
    institution: true,
  },
  'no-show': {
    type: 'no-show',
    code: 'C61',
    businessCode: 'B21',
    title: 'Binmeyen yolcu',
    question: 'Durakta beklenip binmeyen yolcu ne kadar, önceden haber veriliyor mu?',
    icon: 'x',
    group: 'CAPACITY',
    institution: true,
  },
  'eta-accuracy': {
    type: 'eta-accuracy',
    code: 'C62',
    businessCode: 'B21',
    title: 'Tahmini varış doğruluğu',
    question: 'Yolcuya söylenen varış dakikası gerçeğe ne kadar yakın?',
    icon: 'navigation',
    group: 'QUALITY',
    institution: true,
  },
  'vehicle-utilization': {
    type: 'vehicle-utilization',
    code: 'C63',
    businessCode: null,
    title: 'Araç kullanımı',
    question: 'Hangi araç ne kadar yol yaptı, boşta bekleyen araç var mı?',
    icon: 'arac',
    group: 'CAPACITY',
    institution: false,
  },
  'driver-performance': {
    type: 'driver-performance',
    code: 'C64',
    businessCode: null,
    title: 'Şoför performansı',
    question: 'Şoförler dakik mi, yolcular ne puan veriyor, şikayet var mı?',
    icon: 'sofor',
    group: 'CAPACITY',
    institution: false,
  },
  complaints: {
    type: 'complaints',
    code: 'C65',
    businessCode: 'B21',
    title: 'Şikayet raporu',
    question: 'Yolcular neden şikayet ediyor, şikayetler ne kadar sürede çözülüyor?',
    icon: 'uyari',
    group: 'QUALITY',
    institution: true,
  },
  'route-performance': {
    type: 'route-performance',
    code: 'C66',
    businessCode: 'B21',
    title: 'Rota performansı',
    question: 'Hangi rota planına uyuyor, hangisi sorun çıkarıyor?',
    icon: 'rota',
    group: 'QUALITY',
    institution: true,
  },
  'cost-per-passenger': {
    type: 'cost-per-passenger',
    code: 'C67',
    businessCode: 'B21',
    title: 'Yolcu başı maliyet',
    question: 'Bir yolcuyu bir kez taşımak kaça mal oluyor?',
    icon: 'finans',
    group: 'COST',
    institution: true,
    flag: 'FINANCE',
  },
  co2: {
    type: 'co2',
    code: 'C68',
    businessCode: 'B22',
    title: 'CO₂ (ESG)',
    question: 'Servis taşımacılığı, herkesin kendi otomobiliyle gelmesine göre ne kadar emisyon önledi?',
    icon: 'spark',
    group: 'COST',
    institution: true,
  },
};

/**
 * Rapor kodu → kullanıcıya görünen Türkçe adres parçası ve dışa aktarma dosya adı (panel `/raporlar/zamaninda-varis`,
 * dosya `serota-zamaninda-varis-…`). API uç noktası ve kod İngilizce `ReportType` ile çalışır; tek kaynak burasıdır.
 */
export const REPORT_SLUG: Record<ReportType, string> = {
  'on-time': 'zamaninda-varis',
  occupancy: 'doluluk',
  'no-show': 'binmeyen-yolcu',
  'eta-accuracy': 'tahmini-varis-dogrulugu',
  'vehicle-utilization': 'arac-kullanimi',
  'driver-performance': 'sofor-performansi',
  complaints: 'sikayetler',
  'route-performance': 'rota-performansi',
  'cost-per-passenger': 'yolcu-basi-maliyet',
  co2: 'co2',
};

const REPORT_BY_SLUG = new Map(Object.entries(REPORT_SLUG).map(([type, slug]) => [slug, type as ReportType]));

/** Adresteki Türkçe rapor adı → rapor kodu (bilinmeyen ad: null). */
export const reportTypeFromSlug = (slug: string): ReportType | null => REPORT_BY_SLUG.get(slug) ?? null;

export function isReportType(v: string): v is ReportType {
  return (REPORT_TYPES as readonly string[]).includes(v);
}

/** Hazır dönemler (gün). Dönem bugünü de kapsar; karşılaştırma aynı uzunlukta önceki dönemle yapılır. */
export const REPORT_PERIODS = [7, 30, 90] as const;
export const REPORT_MAX_DAYS = 366;

// ───────── CO₂ varsayımları (ekranda ve PDF'te aynen yazılır) ─────────

/** Kişi başı otomobil senaryosu: binek otomobil ortalaması (g CO₂/km). */
export const CO2_CAR_G_PER_KM = 170;
/** Yolcular güzergâh boyunca bindiği için ortalama yolculuk, sefer mesafesinin yarısı kabul edilir. */
export const CO2_RIDE_SHARE = 0.5;
export const CO2_ASSUMPTION =
  `Servis olmasaydı her yolcu kendi otomobiliyle tek başına gelirdi; otomobil ` +
  `${CO2_CAR_G_PER_KM} g CO₂/km salar ve ortalama yolculuk sefer mesafesinin yarısıdır. ` +
  `Servis emisyonu her seferde aracın kayıtlı g CO₂/km değeri × gidilen km ile hesaplanır.`;

// ───────── girdiler ─────────

const OptId = z.string().trim().max(64).optional();

export const ReportQuery = z.object({
  from: IsoDate.optional(),
  to: IsoDate.optional(),
  institutionId: OptId,
  routeId: OptId,
  direction: z.enum(DIRECTIONS).optional(),
});
export type ReportQuery = z.infer<typeof ReportQuery>;

export const ReportExportQuery = ReportQuery.extend({ format: z.enum(['pdf', 'xlsx']) });
export type ReportExportQuery = z.infer<typeof ReportExportQuery>;

export const ReportCatalogQuery = z.object({ from: IsoDate.optional(), to: IsoDate.optional() });

export const SlaQuery = z.object({ from: IsoDate.optional(), to: IsoDate.optional(), contractId: OptId });
export type SlaQuery = z.infer<typeof SlaQuery>;

export const MonthlyReportInput = z.object({
  month: z
    .string()
    .regex(/^\d{4}-(0[1-9]|1[0-2])$/, 'Ay YYYY-AA biçiminde olmalı')
    .optional(),
  /** aynı ay için daha önce gönderildiyse de yeniden gönder */
  force: z.boolean().default(false),
});
export type MonthlyReportInput = z.input<typeof MonthlyReportInput>;

// ───────── rapor yanıtı ─────────

export type ReportUnit = 'pct' | 'count' | 'ratio' | 'min' | 'sec' | 'hours' | 'km' | 'kg' | 'g' | 'money' | 'stars';
export type ReportColumnKind = ReportUnit | 'text' | 'date' | 'time' | 'plate';
export type ReportColor = 'ink' | 'brand' | 'success' | 'warning' | 'danger' | 'route' | 'muted';

export interface ReportKpi {
  key: string;
  label: string;
  unit: ReportUnit;
  value: number | null;
  /** aynı uzunluktaki önceki dönem */
  previous: number | null;
  /** hangi yönde değişim iyi */
  better: 'up' | 'down' | 'none';
  /** sözleşme hedefi (varsa) */
  target?: number | null;
  hint?: string | null;
}

export interface ReportSeries {
  name: string;
  values: (number | null)[];
  color: ReportColor;
  /** yatay çubukta değer başına anlamsal renk (ör. hedef altı `warning`) */
  colors?: ReportColor[];
}

export interface ReportChart {
  key: string;
  title: string;
  sub?: string | null;
  kind: 'line' | 'bar' | 'hbar';
  unit: ReportUnit;
  /** `date`: etiketler ISO gün (istemci "28 Eyl" biçimler) */
  labelKind: 'date' | 'text';
  labels: string[];
  series: ReportSeries[];
  stacked?: boolean;
  target?: { value: number; label: string } | null;
}

export interface ReportColumn {
  key: string;
  header: string;
  kind: ReportColumnKind;
  /** 1024 altında gizlenebilir ikincil kolon */
  secondary?: boolean;
  /** ekranda başlangıçta gizli (kolon menüsünden açılır); dışa aktarmada her zaman var */
  hidden?: boolean;
}

export type ReportCell = string | number | null;

export interface ReportTable {
  title: string;
  columns: ReportColumn[];
  rows: Record<string, ReportCell>[];
  /** satır tıklanınca açılacak kayıt: `key` kolonundaki kimlik */
  link?: { kind: 'trip' | 'route' | 'driver' | 'vehicle' | 'complaint'; key: string } | null;
  empty: string;
}

export interface ReportFilterOptions {
  /** Control: sözleşmeli kurumlar; kurum bağlamında boş (süzgeç gizlenir) */
  institutions: { id: string; name: string }[];
  routes: { id: string; code: string; name: string; institutionId: string }[];
}

export interface ReportPeriod {
  from: string;
  to: string;
  days: number;
}

export interface ReportResult {
  type: ReportType;
  code: string;
  title: string;
  question: string;
  /** firma/kurum adı */
  scope: string;
  /** süzgeç özeti: "Marmara Teknoloji · R14 · Gidiş" (boşsa tümü) */
  filterText: string | null;
  period: ReportPeriod;
  previous: ReportPeriod;
  kpis: ReportKpi[];
  charts: ReportChart[];
  table: ReportTable;
  notes: string[];
  /** rapora özel ek sayılar (ör. CO₂: otomobil senaryosunda yolculuk başı gram) */
  facts?: Record<string, number | null>;
  options: ReportFilterOptions;
  generatedAt: string;
}

export interface ReportCatalogItem {
  type: ReportType;
  code: string;
  title: string;
  question: string;
  icon: ReportMeta['icon'];
  group: ReportGroup;
  headline: { label: string; value: number | null; unit: ReportUnit };
}

export interface ReportCatalog {
  scope: string;
  period: ReportPeriod;
  items: ReportCatalogItem[];
}

// ───────── SLA (B19, B01) ─────────

export type SlaTargetKey = 'ON_TIME' | 'TRACKING' | 'MAX_DELAY' | 'COMPLETION';

export interface SlaTarget {
  key: SlaTargetKey;
  label: string;
  unit: 'pct' | 'min';
  /** sözleşme hedefi; tamamlanma oranında hedef yok */
  target: number | null;
  actual: number | null;
  met: boolean | null;
  hint: string;
}

export interface SlaContractOption {
  id: string;
  number: string;
  provider: string;
  institution: string;
  /** contractState: ACTIVE | EXPIRING | UPCOMING | EXPIRED … */
  state: string;
  trips: number;
}

export interface SlaDay {
  date: string;
  trips: number;
  cancelled: number;
  onTimePct: number | null;
  trackedPct: number | null;
  maxDelayMin: number | null;
}

export interface SlaBreach {
  tripId: string;
  date: string;
  plannedStartAt: string;
  direction: Direction;
  routeId: string;
  routeCode: string;
  routeName: string;
  plate: string;
  delayMin: number;
  /** sınırın üzerindeki dakika */
  overMin: number;
}

export interface SlaRouteRow {
  routeId: string;
  code: string;
  name: string;
  trips: number;
  onTimePct: number | null;
  trackedPct: number | null;
  breaches: number;
  avgDelayMin: number | null;
  maxDelayMin: number | null;
}

export interface SlaSummary {
  /** dönemde sefer yapan araç */
  vehicles: number;
  /** kurumun aktif yolcusu */
  passengers: number;
  occupancyPct: number | null;
  ratingAvg: number | null;
  ratingCount: number;
  ratingPrevAvg: number | null;
}

export interface SlaReport {
  period: ReportPeriod;
  contracts: SlaContractOption[];
  contract: {
    id: string;
    number: string;
    provider: string;
    institution: string;
    startsAt: string;
    endsAt: string;
    slaOnTimePct: number;
    slaGpsPct: number;
    slaMaxDelayMin: number;
  } | null;
  /** business/metrics `servicePerformance` ile aynı ölçüm (sözleşme ekranıyla aynı sayılar) */
  performance: ServicePerformance | null;
  targets: SlaTarget[];
  totals: { completed: number; cancelled: number; completionPct: number | null; breaches: number };
  daily: SlaDay[];
  breaches: SlaBreach[];
  byRoute: SlaRouteRow[];
  summary: SlaSummary;
}

export interface MonthlyReportResult {
  month: string;
  institutions: { id: string; name: string; recipients: number; sent: number; skipped: string | null }[];
  sent: number;
}
